import { Color } from "three";

/** Cor linear de um vértice (r, g, b). */
export type ColorAt = (i: number) => readonly [number, number, number];

/** Luminância de uma cor linear (Rec. 709, a do three). */
export const luma = (r: number, g: number, b: number) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/** Luminância média dos vértices com máscara (ponderada por ela); `undefined` sem nenhum. */
export function meanLuma(count: number, color: ColorAt, mask: (i: number) => number): number | undefined {
  let sum = 0;
  let weight = 0;
  for (let i = 0; i < count; i++) {
    const m = mask(i);
    if (m <= 0) continue;
    sum += m * luma(...color(i));
    weight += m;
  }
  return weight > 0 && sum > 0 ? sum / weight : undefined;
}

/**
 * Cores com as partes da máscara na cor `hex` (sRGB), mantendo o claro e o escuro do atlas (F47):
 * `mix(c, cor·luma(c)/lumaRef, máscara)`, com `lumaRef` a luminância média das partes tingíveis (essa
 * média vira a cor pedida). Onde o claro passaria de 1 num canal, a cor inteira é limitada pelo maior
 * canal: o brilho satura sem mudar o matiz. Devolve rgb linear, 3 por vértice.
 */
export function tintColors(count: number, color: ColorAt, mask: (i: number) => number, hex: string, lumaRef: number): Float32Array {
  const tint = new Color(hex);
  const peak = Math.max(tint.r, tint.g, tint.b);
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const [r, g, b] = color(i);
    const m = mask(i);
    const shade = luma(r, g, b) / lumaRef;
    const k = Math.min(shade, peak > 0 ? 1 / peak : shade);
    out[i * 3] = r + (tint.r * k - r) * m;
    out[i * 3 + 1] = g + (tint.g * k - g) * m;
    out[i * 3 + 2] = b + (tint.b * k - b) * m;
  }
  return out;
}
